import type { AxiosInstance } from 'axios';
import type { Invitation, Team } from '@/types';
import type { TeamRole, TeamWithMembers, TeamWithOwner } from '@/types/settings';
import baseAxios from '@/helpers/forms/axios';

/**
 * Every team read. Spark mounts these under a configurable prefix, so each
 * method takes the `teamsPrefix` from `AppConfig` as an optional trailing
 * argument and falls back to Spark's own default.
 *
 * `/settings/{teamsPrefix}` eager loads each team's owner, `/json/{id}` also
 * eager loads the membership pivots, and `/roles` answers an array of
 * `{ value, text }` pairs rather than a keyed object.
 */
interface TeamsApi {
    fetchTeams: (teamsPrefix?: string) => Promise<TeamWithOwner[]>
    fetchCurrentTeam: (teamsPrefix?: string) => Promise<Team>
    fetchTeam: (teamId: number, teamsPrefix?: string) => Promise<TeamWithMembers>
    fetchRoles: (teamsPrefix?: string) => Promise<TeamRole[]>
    fetchInvitations: (teamId: number, teamsPrefix?: string) => Promise<Invitation[]>
    fetchPendingInvitations: () => Promise<Invitation[]>
    switchCurrentTeam: (teamId: number, teamsPrefix?: string) => Promise<void>
}

export default (axios: AxiosInstance = baseAxios): TeamsApi => ({
    fetchTeams: async (teamsPrefix: string = 'teams'): Promise<TeamWithOwner[]> => {
        const response = await axios.get<TeamWithOwner[]>(`/settings/${teamsPrefix}`);
        return response.data;
    },
    fetchCurrentTeam: async (teamsPrefix: string = 'teams'): Promise<Team> => {
        const response = await axios.get<Team>(`/settings/${teamsPrefix}/current`);
        return response.data;
    },
    fetchTeam: async (teamId: number, teamsPrefix: string = 'teams'): Promise<TeamWithMembers> => {
        const response = await axios.get<TeamWithMembers>(`/settings/${teamsPrefix}/json/${teamId}`);
        return response.data;
    },
    fetchRoles: async (teamsPrefix: string = 'teams'): Promise<TeamRole[]> => {
        const response = await axios.get<TeamRole[]>(`/settings/${teamsPrefix}/roles`);
        return response.data;
    },
    fetchInvitations: async (teamId: number, teamsPrefix: string = 'teams'): Promise<Invitation[]> => {
        const response = await axios.get<Invitation[]>(`/settings/${teamsPrefix}/${teamId}/invitations`);
        return response.data;
    },
    // Not team scoped, so this one route carries no prefix.
    fetchPendingInvitations: async (): Promise<Invitation[]> => {
        const response = await axios.get<Invitation[]>('/settings/invitations/pending');
        return response.data;
    },
    switchCurrentTeam: async (teamId: number, teamsPrefix: string = 'teams'): Promise<void> => {
        await axios.get(`/settings/${teamsPrefix}/${teamId}/switch`);
    },
});
